import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Domain } from "./types";
import { domains } from "./data";
import { practicalExamples } from "./data";
import Tooltip from "../../components/Tooltip";

export default function Operations() {
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-xl font-bold tracking-tight text-gray-800 sm:text-2xl">The 5 Domains of Human Function</h2>
        <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">How to optimize performance and harness stress in your organization</p>
      </div>

      {/* Mobile Dropdown */}
      <div className="lg:hidden mb-4">
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="w-full flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 shadow-sm"
        >
          <div className="flex items-center gap-3">
            {selectedDomain ? (
              <>
                <span className="p-2 rounded-lg bg-blue-50 text-blue-600">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    {/* Use the selected domain's icon */}
                    {selectedDomain.iconId === "target" && (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7.5 3.75H6A2.25 2.25 0 003.75 6v1.5M16.5 3.75H18A2.25 2.25 0 0120.25 6v1.5m0 9V18A2.25 2.25 0 0118 20.25h-1.5m-9 0H6A2.25 2.25 0 013.75 18v-1.5M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    )}
                    {/* Add other icon conditions */}
                  </svg>
                </span>
                <span className="font-medium text-gray-900">{selectedDomain.title}</span>
              </>
            ) : (
              <span className="text-gray-500">Select a domain</span>
            )}
          </div>
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown Menu */}
        {/* @ts-ignore */}
        <AnimatePresence>
          {isDropdownOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute z-10 mt-2 w-full bg-white rounded-lg border border-gray-200 shadow-lg"
            >
              <div className="p-2">
                <div className="mb-2">
                  <h3 className="px-3 py-2 text-sm font-semibold text-gray-500">Primary Domains</h3>
                  {domains
                    .filter(domain => domain.isPrimary)
                    .map(domain => (
                      <button
                        key={domain.id}
                        onClick={() => {
                          setSelectedDomain(domain);
                          setIsDropdownOpen(false);
                        }}
                        className={`
                          w-full text-left px-3 py-2 rounded-lg transition-colors
                          ${selectedDomain?.id === domain.id ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}
                        `}
                      >
                        <div className="flex items-center gap-3">
                          <span className="p-1.5 rounded-lg bg-gray-50">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              {/* Add domain icons */}
                            </svg>
                          </span>
                          {domain.title}
                        </div>
                      </button>
                    ))}
                </div>

                <div>
                  <h3 className="px-3 py-2 text-sm font-semibold text-gray-500">Secondary Domains</h3>
                  {domains
                    .filter(domain => !domain.isPrimary)
                    .map(domain => (
                      <button
                        key={domain.id}
                        onClick={() => {
                          setSelectedDomain(domain);
                          setIsDropdownOpen(false);
                        }}
                        className={`
                          w-full text-left px-3 py-2 rounded-lg transition-colors
                          ${selectedDomain?.id === domain.id ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}
                        `}
                      >
                        <div className="flex items-center gap-3">
                          <span className="p-1.5 rounded-lg bg-gray-50">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              {/* Add domain icons */}
                            </svg>
                          </span>
                          {domain.title}
                        </div>
                      </button>
                    ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="h-[85vh] bg-gradient-to-br from-slate-50 to-white border rounded-lg border-gray-200 shadow-sm overflow-hidden">
        <div className="h-full flex">
          {/* Desktop Left Panel - Hide on mobile */}
          <div className="hidden lg:flex w-[420px] min-w-[420px] h-full border-r border-gray-300 flex-col bg-gradient-to-b from-blue-50/30 to-slate-50/30">
            <div className="p-6 flex flex-col relative h-full">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-100/20 rounded-full blur-3xl -z-10" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-slate-100/30 rounded-full blur-3xl -z-10" />

              {/* Primary Domains Section */}
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-6 flex items-center">
                  <span className="w-8 h-[2px] bg-blue-500 mr-3" />
                  Primary Domains
                </h3>
                <div className="space-y-3">
                  {domains
                    .filter((domain) => domain.isPrimary)
                    .map((domain) => (
                      <motion.button
                        key={domain.id}
                        onClick={() => setSelectedDomain(domain)}
                        className={`
                          w-full text-left p-4 rounded-xl transition-all border relative
                          ${selectedDomain?.id === domain.id 
                            ? "bg-white border-blue-200 shadow-md shadow-blue-100" 
                            : "bg-white/70 border-slate-200 hover:border-blue-200 hover:bg-white hover:shadow-sm"}
                          group
                        `}
                        whileHover={{ x: 4, scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <div className="flex items-center gap-4">
                          <span className={`
                            p-2 rounded-lg transition-colors
                            ${selectedDomain?.id === domain.id 
                              ? "bg-blue-50 text-blue-600" 
                              : "text-slate-600 group-hover:text-blue-600"}
                          `}>
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                              {domain.iconId === "sparkles" && (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
                                />
                              )}
                              {domain.iconId === "users" && (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
                                />
                              )}
                              {domain.iconId === "puzzle" && (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42"
                                />
                              )}
                              {domain.iconId === "heart" && (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
                                />
                              )}
                              {domain.iconId === "work" && (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z"
                                />
                              )}
                            </svg>
                          </span>
                          <div>
                            <h3 className="font-medium text-slate-900 mb-1">{domain.title}</h3>
                            <p className={`text-sm ${
                              selectedDomain?.id === domain.id 
                                ? "text-blue-600" 
                                : "text-slate-500"
                            }`}>{domain.shortDescription}</p>
                          </div>

                        </div>
                      </motion.button>
                    ))}
                </div>
              </div>

              {/* Secondary Domains Section */}
              <div>
                <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-6 flex items-center">
                  <span className="w-8 h-[2px] bg-slate-400 mr-3" />
                  Secondary Domains
                </h3>
                <div className="space-y-3">
                  {domains
                    .filter((domain) => !domain.isPrimary)
                    .map((domain) => (
                      <motion.button
                        key={domain.id}
                        onClick={() => setSelectedDomain(domain)}
                        className={`
                          w-full text-left p-4 rounded-xl transition-all border relative
                          ${selectedDomain?.id === domain.id 
                            ? "bg-white border-slate-300 shadow-md" 
                            : "bg-white/70 border-slate-200 hover:border-slate-300 hover:bg-white hover:shadow-sm"}
                          group
                        `}
                        whileHover={{ x: 4, scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <div className="flex items-center gap-4">
                          <span className={`
                            p-2 rounded-lg transition-colors
                            ${selectedDomain?.id === domain.id 
                              ? "bg-blue-50 text-blue-600" 
                              : "text-slate-600 group-hover:text-blue-600"}
                          `}>
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                              {domain.iconId === "target" && (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M7.5 3.75H6A2.25 2.25 0 003.75 6v1.5M16.5 3.75H18A2.25 2.25 0 0120.25 6v1.5m0 9V18A2.25 2.25 0 0118 20.25h-1.5m-9 0H6A2.25 2.25 0 013.75 18v-1.5M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                              )}
                              {domain.iconId === "sparkles" && (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
                                />
                              )}
                              {domain.iconId === "users" && (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
                                />
                              )}
                              {domain.iconId === "puzzle" && (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42"
                                />
                              )}
                              {domain.iconId === "heart" && (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
                                />
                              )}
                            </svg>
                          </span>
                          <div>
                            <h3 className="font-medium text-slate-900 mb-1">{domain.title}</h3>
                            <p className={`text-sm ${
                              selectedDomain?.id === domain.id 
                                ? "text-blue-600" 
                                : "text-slate-500"
                            }`}>{domain.shortDescription}</p>
                          </div>
                        </div>
                      </motion.button>
                    ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Domain Content */}
          <div className="flex-1 h-full overflow-y-auto bg-gradient-to-br from-white to-slate-50 relative">
            {/* Scroll Indicator - Top */}
            <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-slate-100/50 to-transparent pointer-events-none z-10" />
            
            {/* Scroll Indicator - Bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-slate-100/50 to-transparent pointer-events-none z-10" />
            
            {/* @ts-ignore */}
            <AnimatePresence mode="wait">
              {selectedDomain ? (
                <DomainContent key={selectedDomain.id} domain={selectedDomain} />
              ) : (
                <WelcomeScreen />
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

function WelcomeScreen() {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex items-center justify-center p-12 bg-white border-r rounded-lg">
      <div className="max-w-4xl text-center">
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}>
          <span className="text-blue-600 font-semibold mb-4 block">Welcome to</span>
          <h2 className="text-4xl font-bold mb-8 text-gray-900">The Science of Human Performance</h2>
          <div className="mb-12">
            <p className="text-gray-600 text-xl leading-relaxed mb-8">
              Discover how these five research-backed domains work together to create sustainable peak performance and reduce stress in organizations.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-8 rounded-2xl border border-gray-200 shadow-sm">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="p-8">
              <div className="text-4xl mb-4 text-blue-600">🎯</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Evidence-Based</h3>
              <p className="text-gray-600">Built on decades of research and practical application</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="p-8">
              <div className="text-4xl mb-4 text-blue-600">⚡️</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Immediate Impact</h3>
              <p className="text-gray-600">See results within weeks of implementation</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="p-8">
              <div className="text-4xl mb-4 text-blue-600">🔄</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Sustainable</h3>
              <p className="text-gray-600">Create lasting change through systematic approach</p>
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-10 text-gray-500 flex items-center justify-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
            </svg>
            <span>Select any domain from the left to begin exploring</span>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );
}

function DomainContent({ domain }: { domain: Domain }) {
  return (
    <motion.div 
      initial={{ opacity: 0, x: 20 }} 
      animate={{ opacity: 1, x: 0 }} 
      exit={{ opacity: 0, x: -20 }} 
      className="h-full overflow-y-auto relative"
    >
      {/* Scroll Indicator */}
      <motion.div 
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ delay: 2, duration: 0.5 }}
        className="absolute top-4 right-4 bg-white/80 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm border border-slate-200 text-slate-600 pointer-events-none z-20"
      >
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 13l-7 7-7-7m14-8l-7 7-7-7" />
          </svg>
          <span className="text-sm font-medium whitespace-nowrap">Scroll to explore</span>
        </div>
      </motion.div>

      <div className="p-6 md:p-12">
        {/* Domain Header */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }} 
          animate={{ y: 0, opacity: 1 }}
          className=" bg-white mb-6"
        >
          <h2 className="text-3xl font-bold mb-4 text-slate-900">{domain.title}</h2>
          <div className="h-1 w-24 bg-blue-500 rounded-full mb-4" />
          <p className="text-xl text-slate-600">{domain.shortDescription}</p>
        </motion.div>

        {/* Main Idea */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="relative bg-gradient-to-r from-blue-50 to-white rounded-2xl p-8 mb-12 border border-blue-100 shadow-sm"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-200 opacity-10 rounded-full transform translate-x-16 -translate-y-16" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-[#3c82f6] text-lg font-semibold">Main Idea</h3>
            </div>
            <p className="text-gray-800 text-lg leading-relaxed">{domain.mainIdea}</p>
          </div>
        </motion.div>

        {/* Content Grid - Update to be mobile responsive */}
        <div className="space-y-8">
          {/* Key Points - Full Width */}
          <motion.section 
            initial={{ y: 20, opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }} 
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <div className="flex items-center gap-2 mb-6">
              <h3 className="text-lg font-semibold text-[#3c82f6]">Key Points</h3>
              <div className="flex">
                <Tooltip message="Essential elements for understanding this domain" />
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {domain.keyPoints.map((point, index) => (
                <div 
                  key={index} 
                  className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:border-blue-300 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold">
                      {index + 1}
                    </div>
                    <p className="text-base text-gray-700">{point}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>

          {/* Leadership Actions */}
          <motion.section 
            initial={{ y: 20, opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }} 
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-2 mb-6">
              <h3 className="text-lg font-semibold text-[#3c82f6]">Leadership Questions</h3>
              <div className="flex">
                <Tooltip message="Practical questions leaders can ask to implement this domain" />
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {domain.leadershipActions.map((action, index) => (
                <div 
                  key={index} 
                  className="group bg-gradient-to-br from-white to-slate-50 p-5 rounded-xl border border-gray-200 shadow-sm hover:border-blue-300 hover:shadow-md transition-all"
                >
                  <div className="flex flex-col gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <p className="text-base text-gray-700">{action}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>
        </div>

        {/* Implementation Guide - Mobile optimized */}
        <motion.section 
          initial={{ y: 20, opacity: 0 }} 
          animate={{ y: 0, opacity: 1 }} 
          transition={{ delay: 0.4 }} 
          className="mt-8 lg:mt-12"
        >
          <div className="flex items-center gap-2 mb-4 lg:mb-4">
            <h3 className="text-base lg:text-lg font-semibold text-[#3c82f6]">Example Scenarios</h3>
            <div className="flex">
              <Tooltip message="These are illustrative examples to demonstrate how this domain can be applied in practice" />
            </div>
          </div>
          
          <div className="bg-blue-50/50 p-4 rounded-lg mb-6">
            <div className="flex items-start gap-3">
              <p className="text-sm text-blue-800">
                The following scenarios are fictional examples designed to illustrate common challenges and potential solutions. 
                Use these as inspiration for developing solutions tailored to your specific situation.
              </p>
            </div>
          </div>
          
          <div className="space-y-4 lg:space-y-6">
            {practicalExamples[domain.id]?.map((example, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:border-blue-200 transition-colors"
              >
                {/* Header */}
                <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white p-3 lg:px-6 lg:py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between">
                    <div className="flex items-center gap-2 lg:gap-3">
                      <span className="flex items-center gap-2">
                        <span className="text-xs uppercase tracking-wider text-blue-600 font-semibold">SCENARIO</span>
                      </span>
                      <h4 className="text-sm lg:text-base font-medium text-gray-900">{example.scenario}</h4>
                    </div>
                    <span className="text-xs lg:text-sm text-blue-600 font-medium px-2 py-1 lg:px-3 lg:py-1 bg-blue-50 rounded-full self-start sm:self-auto">
                      {domain.title}
                    </span>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-3 lg:p-6 space-y-4 lg:space-y-6">
                  {/* Challenge Section */}
                  <div className="flex gap-3 lg:gap-4">
                    <div className="flex-shrink-0 w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-red-50 text-red-600 flex items-center justify-center">
                      <svg className="w-5 h-5 lg:w-6 lg:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-1 lg:mb-2">Challenge</h5>
                      <p className="text-sm lg:text-base text-gray-600">{example.challenge}</p>
                    </div>
                  </div>

                  {/* Solution Section */}
                  <div className="flex gap-3 lg:gap-4">
                    <div className="flex-shrink-0 w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-green-50 text-green-600 flex items-center justify-center">
                      <svg className="w-5 h-5 lg:w-6 lg:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-1 lg:mb-2">Solution</h5>
                      <p className="text-sm lg:text-base text-gray-600">{example.solution}</p>
                    </div>
                  </div>

                  {/* Outcomes Section */}
                  <div className="flex gap-3 lg:gap-4">
                    <div className="flex-shrink-0 w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-yellow-50 text-yellow-600 flex items-center justify-center">
                      <svg className="w-5 h-5 lg:w-6 lg:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-1 lg:mb-2">Measured Outcomes</h5>
                      <ul className="space-y-1 lg:space-y-2">
                        {example.outcomes.map((outcome, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm lg:text-base text-gray-600">
                            <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>{outcome}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.section>
      </div>
    </motion.div>
  );
}
